import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from './ui/collapsible';
import {
  AlertTriangle,
  TrendingUp,
  Package,
  FolderOpen,
  DollarSign,
  CheckCircle,
  XCircle,
  TrendingDown,
  ArrowLeftRight,
  Globe,
  Building2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import InventoryMetricCard from './InventoryMetricCard';

/**
 * Formal ERP-style Inventory Overview Component
 * Displays comprehensive inventory metrics with professional styling
 */
const InventoryOverview = React.memo(({
  stats,
  onClearAllFilters,
  onSetFilter,
  onSetSearchMode,
  onSetAdvancedFiltersOpen,
  onFilterSubmit,
  onExportInventory
}) => {
  // State for collapsible sections
  const [healthExpanded, setHealthExpanded] = React.useState(false);
  const [activityExpanded, setActivityExpanded] = React.useState(false);

  // Memoized click handlers to prevent recreating on every render
  const handleViewAll = React.useCallback(() => {
    onClearAllFilters();
    onSetSearchMode('basic');
  }, [onClearAllFilters, onSetSearchMode]);

  const handleViewAvailable = React.useCallback(() => {
    onSetSearchMode('advanced');
    onSetAdvancedFiltersOpen(true);
    onSetFilter({
      name: '',
      category: '',
      product_id: '',
      part_number: '',
      material: '',
      coating: '',
      stock_status: '有库存',
      price_range: { min: '', max: '' },
      diameter_range: { min: '', max: '', unit: 'mm' },
      min_stock: '',
      max_stock: '',
      location: ''
    });
  }, [onSetFilter, onSetSearchMode, onSetAdvancedFiltersOpen]);

  const handleViewLowStock = React.useCallback(() => {
    onSetSearchMode('advanced');
    onSetAdvancedFiltersOpen(true);
    onSetFilter({
      name: '',
      category: '',
      product_id: '',
      part_number: '',
      material: '',
      coating: '',
      stock_status: '库存不足',
      price_range: { min: '', max: '' },
      diameter_range: { min: '', max: '', unit: 'mm' },
      min_stock: '',
      max_stock: '',
      location: ''
    });
  }, [onSetFilter, onSetSearchMode, onSetAdvancedFiltersOpen]);

  const handleViewOutOfStock = React.useCallback(() => {
    onSetSearchMode('advanced');
    onSetAdvancedFiltersOpen(true);
    onSetFilter({
      name: '',
      category: '',
      product_id: '',
      part_number: '',
      material: '',
      coating: '',
      stock_status: '无库存',
      price_range: { min: '', max: '' },
      diameter_range: { min: '', max: '', unit: 'mm' },
      min_stock: '',
      max_stock: '',
      location: ''
    });
  }, [onSetFilter, onSetSearchMode, onSetAdvancedFiltersOpen]);

  const handleLowStockButton = React.useCallback(() => {
    onSetSearchMode('advanced');
    onSetAdvancedFiltersOpen(true);
    onSetFilter({
      name: '',
      category: '',
      product_id: '',
      part_number: '',
      material: '',
      coating: '',
      stock_status: '库存不足',
      price_range: { min: '', max: '' },
      diameter_range: { min: '', max: '' },
      min_stock: '',
      max_stock: '',
      location: ''
    });
  }, [onSetFilter, onSetSearchMode, onSetAdvancedFiltersOpen]);

  // Early return if no stats to prevent unnecessary renders
  if (!stats) return null;

  // Format currency values
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `¥${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `¥${(value / 1000).toFixed(1)}K`;
    } else {
      return `¥${value.toFixed(0)}`;
    }
  };

  // Calculate stock health percentage
  const totalStockItems = stats.total_items || 1;
  const healthPercentage = Math.round((stats.healthy_stock_count / totalStockItems) * 100);
  const lowStockPercentage = Math.round((stats.low_stock_count / totalStockItems) * 100);

  // Determine health status
  const getHealthStatus = () => {
    if (healthPercentage >= 70) return { text: '健康', variant: 'default' };
    if (healthPercentage >= 50) return { text: '良好', variant: 'secondary' };
    if (healthPercentage >= 30) return { text: '警告', variant: 'outline' };
    return { text: '风险', variant: 'destructive' };
  };

  const healthStatus = getHealthStatus();

  return (
    <Card className="mb-6 border bg-card">
      <CardContent className="p-6">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
              <Package className="h-7 w-7" />
            </div>
            <div>
              <h2 className="text-xl font-bold">库存概览</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="gap-1">
                  {stats.is_global_view ? <Globe className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                  {stats.is_global_view ? '全局视图' : '客户视图'}
                </Badge>
                <Badge variant={healthStatus.variant} className="font-semibold">
                  健康度: {healthPercentage}%
                </Badge>
                <span className="text-xs text-muted-foreground">
                  最后更新: {new Date().toLocaleString('zh-CN')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-warning border-warning/50 hover:bg-warning/10"
              onClick={handleLowStockButton}
              aria-label={`View ${stats.low_stock_count} low stock items`}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              库存不足 ({stats.low_stock_count})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onExportInventory}
              aria-label="Export inventory data"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              导出数据
            </Button>
          </div>
        </div>

        <Separator className="mb-6" />

        {/* Core Metrics Section */}
        <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">
          核心指标
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6" role="region" aria-label="Core inventory metrics">
          <InventoryMetricCard
            title="总SKU数"
            value={stats.total_items?.toLocaleString()}
            subtitle="产品种类数量"
            icon={<Package className="h-5 w-5" />}
            color="text-primary"
            bgColor="bg-primary/10"
            borderColor="border-primary/20"
            tooltipTitle="Click to view all inventory items"
            onClick={handleViewAll}
          />

          <InventoryMetricCard
            title="可用库存"
            value={stats.total_remaining?.toLocaleString()}
            subtitle="可销售数量"
            icon={<CheckCircle className="h-5 w-5" />}
            color="text-success"
            bgColor="bg-success/10"
            borderColor="border-success/20"
            tooltipTitle="Click to view available stock"
            onClick={handleViewAvailable}
          />

          <InventoryMetricCard
            title="预留库存"
            value={stats.total_reserved?.toLocaleString()}
            subtitle="已预订数量"
            icon={<ArrowLeftRight className="h-5 w-5" />}
            color="text-info"
            bgColor="bg-info/10"
            borderColor="border-info/20"
            isClickable={false}
          />

          <InventoryMetricCard
            title="库存总值"
            value={formatCurrency(stats.total_value || 0)}
            subtitle="总市场价值"
            icon={<DollarSign className="h-5 w-5" />}
            color="text-warning"
            bgColor="bg-warning/10"
            borderColor="border-warning/20"
            isClickable={false}
          />
        </div>

        <Separator className="mb-6" />

        {/* Stock Health Section - Collapsible */}
        <Collapsible open={healthExpanded} onOpenChange={setHealthExpanded}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase">
              库存健康状态
            </h3>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={healthExpanded ? "收起库存健康状态" : "展开库存健康状态"}
              >
                {healthExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6" role="region" aria-label="Stock health metrics">
              <InventoryMetricCard
                title="健康库存"
                value={stats.healthy_stock_count?.toLocaleString()}
                subtitle={`${healthPercentage}% 库存状态良好`}
                icon={<CheckCircle className="h-5 w-5" />}
                color="text-success"
                bgColor="bg-success/10"
                borderColor="border-success/20"
                isClickable={false}
              />

              <InventoryMetricCard
                title="库存不足"
                value={stats.low_stock_count?.toLocaleString()}
                subtitle={`${lowStockPercentage}% 需要补货`}
                icon={<AlertTriangle className="h-5 w-5" />}
                color="text-warning"
                bgColor="bg-warning/10"
                borderColor="border-warning/20"
                tooltipTitle="Click to filter low stock items"
                onClick={handleViewLowStock}
              />

              <InventoryMetricCard
                title="无库存"
                value={stats.out_of_stock_count?.toLocaleString()}
                subtitle="缺货产品数"
                icon={<XCircle className="h-5 w-5" />}
                color="text-destructive"
                bgColor="bg-destructive/10"
                borderColor="border-destructive/20"
                tooltipTitle="Click to view out of stock items"
                onClick={handleViewOutOfStock}
              />

              <InventoryMetricCard
                title="积压库存"
                value={stats.overstock_count?.toLocaleString()}
                subtitle="可能积压产品"
                icon={<TrendingDown className="h-5 w-5" />}
                color="text-secondary-foreground"
                bgColor="bg-secondary/50"
                borderColor="border-secondary"
                isClickable={false}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Separator className="mb-6" />

        {/* Activity & Analytics Section - Collapsible */}
        <Collapsible open={activityExpanded} onOpenChange={setActivityExpanded}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase">
              近30天活动统计
            </h3>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={activityExpanded ? "收起活动统计" : "展开活动统计"}
              >
                {activityExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" role="region" aria-label="Recent activity metrics">
              <InventoryMetricCard
                title="入库次数"
                value={stats.inbound_movements_30d?.toLocaleString()}
                subtitle="近30天采购入库"
                icon={<TrendingUp className="h-5 w-5" />}
                color="text-success"
                bgColor="bg-success/10"
                borderColor="border-success/10"
                isClickable={false}
              />

              <InventoryMetricCard
                title="出库次数"
                value={stats.outbound_movements_30d?.toLocaleString()}
                subtitle="近30天销售出库"
                icon={<TrendingDown className="h-5 w-5" />}
                color="text-info"
                bgColor="bg-info/10"
                borderColor="border-info/10"
                isClickable={false}
              />

              <InventoryMetricCard
                title="周转率"
                value={`${(stats.turnover_rate_30d * 100).toFixed(1)}%`}
                subtitle="月度库存周转"
                icon={<ArrowLeftRight className="h-5 w-5" />}
                color="text-primary"
                bgColor="bg-primary/10"
                borderColor="border-primary/10"
                isClickable={false}
              />

              <InventoryMetricCard
                title="产品分类"
                value={stats.category_count?.toLocaleString()}
                subtitle="库存类别数量"
                icon={<FolderOpen className="h-5 w-5" />}
                color="text-secondary-foreground"
                bgColor="bg-secondary/50"
                borderColor="border-secondary/50"
                isClickable={false}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
});

InventoryOverview.displayName = 'InventoryOverview';

export default InventoryOverview;
